// Copyright (c) 2024 by Chris Gannon (https://codepen.io/chrisgannon/pen/VwwRGQG)
// Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
// The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.


import React, { useEffect, useState} from 'react';
import './PlaneTrails.css'

import gsap from 'gsap';
import { CustomEase } from 'gsap/CustomEase';

gsap.registerPlugin(CustomEase);

const PlaneTrails= () => {
  // const [viewBox, setViewBox] = useState('0 -150 600 600');

  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const select = (s) => document.querySelector(s);
    const selectAll = (s) => document.querySelectorAll(s);
    const mainSVG = select('#mainSVG');
    const allPlanes = gsap.utils.toArray('.plane');
    const allTrailLines = selectAll('.trailLine');
    let allTrailPoints = [];
    const planeColorArray = ['#04BCE8', '#EA6360', '#4EBE92', '#A83395', '#4A52A6', '#F2CD5D'];
    const planeWidth = allPlanes[0].getBBox().width;
    const pivotFrame = 60;
    const svgHeight = 300;
    const height = svgHeight;
    const numPoints = 400;
    // const window_width = window.innerWidth;
    // const window_height = window.innerHeight;

    gsap.defaults({ lazy: true });

    gsap.set('svg', {
      visibility: 'visible'
    });

    gsap.set(allPlanes, {
      transformOrigin: '50% 50%',
      yPercent: -50
    });

    const p = CustomEase.create('trail', 'M0,0 C0,0 0.08957,0.04997 0.14563,0.07332 0.17361,0.08497 0.19406,0.08816 0.22447,0.09346 0.25234,0.09831 0.27245,0.10281 0.29876,0.1 0.3304,0.09662 0.3574,0.09206 0.38526,0.07627 0.46146,0.0331 0.50906,-0.01658 0.58698,-0.06332 0.61735,-0.08154 0.64322,-0.09168 0.67604,-0.09815 0.70315,-0.10349 0.72556,-0.09999 0.75503,-0.09644 0.7862,-0.09269 0.8064,-0.0881 0.83671,-0.07879 0.87049,-0.06842 0.89148,-0.06013 0.92338,-0.04473 0.95378,-0.03007 1,0 1,0 ');

    // Cleanup function to remove points and animations
    const cleanup = () => {
      gsap.killTweensOf(allPlanes);
      gsap.killTweensOf(allTrailLines);
      gsap.killTweensOf('#grid');

      // Remove all previous points from trail lines
      allTrailLines.forEach(line => {
        while (line.points.length > 0) {
          line.points.removeItem(0);
        }
      });

      allTrailPoints = []; // Clear points
    };

    cleanup(); // Clean up existing points before rerendering


    const swayPlane = (_id) => {
      gsap.set(allPlanes[_id], {
        x: allTrailPoints[_id][0].x - (planeWidth / 2),
        y: allTrailPoints[_id][0].y - planeWidth
      });
      gsap.to(allPlanes[_id], {
        duration: 0.1,
        rotation: ((planeInitArray[_id].x - (allTrailPoints[_id][pivotFrame].x)) * 0.72),
        ease: 'sine.inOut'
      });
    };

    const planeInitArray = [];
    const duration = gsap.utils.random(5, 20);

    for (let j = 0; j < allPlanes.length; j++) {
      const trailLine = allTrailLines[j];
      // const pointX = gsap.utils.random(window.innerWidth/2-100,window.innerWidth/2+100);
      const pointX = gsap.utils.random((width/2-100),(width/2+100));
      //const pointX = gsap.utils.random(500, 600);
      // const pointX = gsap.utils.random(600, 700);//Moves planes to right side of screen
      const pointArray = [];
      const heightMultiplier = gsap.utils.random(0.4, 1.2); //height multiplier


      gsap.set(allTrailLines[j], {
        stroke: planeColorArray[j]
      });

      for (let i = 0; i < numPoints; i++) {
        const point = trailLine.points.appendItem(mainSVG.createSVGPoint());
        pointArray.push(point);
        point.x = pointX;
        point.y = (height * heightMultiplier) + (i * ((height) / numPoints)); 
      }

      allTrailPoints.push(pointArray);
      planeInitArray.push({ x: pointArray[0].x, y: pointArray[0].y });

      const tl = gsap.timeline();
      tl.to(allTrailPoints[j], {
        duration: gsap.utils.random(7, 14),
        x: '-=' + gsap.utils.random(-600, 600),
        stagger: {
          each: duration / 1000,
          repeat: -1
        },
        onUpdate: swayPlane,
        onUpdateParams: [j],
        ease: 'trail'
      }).seek(gsap.utils.random(300, 1000));
    }

    const extraTl = gsap.timeline();
    extraTl.to('#grid', {
      duration: 1,
      y: '+=40',
      ease: 'none',
      repeat: -1
    });

    allTrailLines.forEach((i, c) => {
      const tl = gsap.timeline();
      tl.to(i, {
        strokeDashoffset: -(14 * 4),
        duration: gsap.utils.random(0.5, 0.76),
        repeat: -1,
        ease: 'none'
      });
      extraTl.add(tl, 0);
    });

    //return cleanup;

  }, [width]);

  return (
    <div className='absolute top-0 left-0 w-full h-full -z-1 justify-center items-center'>
      <svg className='absolute w-full h-full block' id="patternSVG" xmlns="http://www.w3.org/2000/svg" style={{ zIndex: -1}}>
        <defs>
          <pattern id="sqrs" width="20" height="20" patternUnits="userSpaceOnUse" viewBox="0 0 20 20">
            <rect className="gridRect" x="0" y="0" width="20" height="20" fill="none" />
          </pattern>
        </defs>
        {/* <rect x="0" y="0" width="100%" height="100%" fill="none" stroke="green" strokeWidth="2" /> */}
        <rect id="grid" className="grid" y="-10" fill="url(#sqrs)" width="100%" height="100%" stroke="none" />
      </svg>
      <svg className="relative w-full h-full top-1/2" id="mainSVG"
            xmlns="http://www.w3.org/2000/svg"
            // viewBox={viewBox}
            //viewBox="0 -500 1000 1000"
            style={{ zIndex: -1 }}
            >
        {/* <rect width="100%" height="100%" fill="none" stroke="red" strokeWidth="1" viewBox="0 0 100 100" /> */}
        <defs>
          <filter id="glow" x="-100%" y="-100%" width="250%" height="250%">
            <feGaussianBlur stdDeviation="8" result="coloredBlur" />
            <feOffset dx="0" dy="0" result="offsetblur"></feOffset>
            <feFlood id="glowAlpha" floodColor="#FFF" floodOpacity="1"></feFlood>
            <feComposite in2="offsetblur" operator="in"></feComposite>
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <g >
          {Array.from({ length: 6 }).map((_, index) => (
            <React.Fragment key={index}>
              <polyline className="trailLine" />
              <path className="plane" d="M20,13.7v-1.4l-8.6-5.1V3.5c0-1-0.6-3.5-1.4-3.5S8.6,2.4,8.6,3.5v3.7L0,12.2v1.4l8.6-1.8l0.5,5l-2.5,1.9V20
                l3.5-0.8l3.5,0.8v-1.2L11,16.9l0.5-5L20,13.7z" />
            </React.Fragment>
          ))}
        </g>
      </svg>
    </div>
  );
};

export default PlaneTrails;

